/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC } from "react"
import { ImageOutlined } from "@duxton-web/icons"
import { Avatar, Typography } from "@duxton-web/ui"

import { useTranslationsMap } from "common/utils/i18n"

import {
  ReturnRequestItem,
  getReturnItemStatusLabel,
  getReturnReasonLabel,
} from "ecommerce/constants/returnRefund"

import styles from "./ReturnRequestItemRow.modules.css"

const { Text } = Typography

interface ReturnRequestItemRowProps {
  item: ReturnRequestItem
}

export const ReturnRequestItemRow: FC<ReturnRequestItemRowProps> = ({
  item,
}) => {
  const text = useTranslationsMap({
    itemStatusAcknowledgingRequest:
      "ecommerce.sellerCentre.returns.itemStatusAcknowledgingRequest",
    itemStatusDeliveringReturnedItem:
      "ecommerce.sellerCentre.returns.itemStatusDeliveringReturnedItem",
    itemStatusRefunded: "ecommerce.sellerCentre.returns.itemStatusRefunded",
    itemStatusRejected: "ecommerce.sellerCentre.returns.itemStatusRejected",
    itemStatusRequestCanceled:
      "ecommerce.sellerCentre.returns.itemStatusRequestCanceled",
    itemStatusReturnRequested:
      "ecommerce.sellerCentre.returns.itemStatusReturnRequested",
    itemStatusVerifyingReturnedItem:
      "ecommerce.sellerCentre.returns.itemStatusVerifyingReturnedItem",
    reasonItemDamaged: "ecommerce.sellerCentre.returns.reasonItemDamaged",
    reasonWrongItem: "ecommerce.sellerCentre.returns.reasonWrongItem",
  })

  // Variant and per-item status share one line, joined only when both are present.
  // Raw-value fallback: statuses are placeholders until the BE enum (Q15).
  const subtitle = [
    item.variant,
    item.status && (getReturnItemStatusLabel(text)[item.status] ?? item.status),
  ]
    .filter(Boolean)
    .join(" · ")

  // Server copy first, the FE map for mock-backed items, the raw code as last resort.
  const reasonLabel =
    item.reasonLabel ??
    (item.reason && (getReturnReasonLabel(text)[item.reason] ?? item.reason))

  return (
    <div className={styles.row}>
      <Avatar
        shape="square"
        src={item.imageURL}
        alt={item.name}
        // Without a fallback, a missing imageURL paints an empty transparent
        // box — the mock items carry no URLs (any invented one would 404).
        icon={<ImageOutlined />}
        className={styles.thumbnail}
      />
      <div className={styles.details}>
        {reasonLabel && (
          <Text type="danger" strong className={styles.small}>
            {reasonLabel}
          </Text>
        )}
        <Text strong>{item.name}</Text>
        {subtitle && <Text className={styles.metadata}>{subtitle}</Text>}
      </div>
      <div className={styles.amounts}>
        <Text type="secondary">{`x${item.quantity}`}</Text>
        <Text className={styles.metadata}>{item.price}</Text>
      </div>
    </div>
  )
}
